import { useContext, useEffect, useState, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Flex, Text, Link, Button } from 'src/components/ui';
import { StringInputHook } from 'src/components/common/form';
import AlertMessage from '../common/AlertMessage';
import authService from 'src/services/auth';
import UserContext from 'src/contexts/user/UserContext';
import { SingUpSchema, ISingUpForm } from 'src/lib/schemas';
import { OtpVerification } from './OtpVerification';

interface CustomWindow extends Window {
	dataLayer: Array<Record<string, any>>;
}
declare const window: CustomWindow;

const SignUpForm = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showOtpVerification, setShowOtpVerification] = useState(false);
	const [userPhone, setUserPhone] = useState('');

	const formMethods = useForm<ISingUpForm>({
		resolver: zodResolver(SingUpSchema),
	});
	const { setUser } = useContext(UserContext);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { executeRecaptcha } = useGoogleReCaptcha();

	const getCaptchaToken = useCallback(async (action: string): Promise<string | undefined> => {
		if (!executeRecaptcha) {
			console.warn('reCAPTCHA not ready');
			return undefined;
		}
		try {
			return await executeRecaptcha(action);
		} catch (err) {
			console.error('reCAPTCHA execution failed:', err);
			return undefined;
		}
	}, [executeRecaptcha]);

	useEffect(() => {
		const pricingIdFromUrl = searchParams.get('pricingId');
		if (pricingIdFromUrl) {
			sessionStorage.setItem('pricingId', pricingIdFromUrl);
		}
		const storedPricingId =
			pricingIdFromUrl || sessionStorage.getItem('pricingId');

		formMethods.reset({
			phone: '',
			name: '',
			accountName: '',
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			pricingId: storedPricingId ?? '',
		});
	}, [formMethods, searchParams]);

	const onSubmit = formMethods.handleSubmit(async (formData) => {
		setError(null);
		setIsLoading(true);
		try {
			const result = await authService.signup(formData);

			// Phone signup should always require OTP
			if ('requiresOtp' in result && result.requiresOtp) {
				setShowOtpVerification(true);
				setUserPhone(formData.phone || '');
				setIsLoading(false);
				return;
			}

			// This shouldn't happen for new signups, but handle just in case
			const user = result as any;
			if (user) {
				window.dataLayer.push({
					event: 'successful_sign_up',
					user_id: user.id,
					phone: user.phone,
					name: user.name,
					account_id: user.account,
				});
				setUser(user);
				navigate('/home');
			}
		} catch (error: any) {
			const { message } = error.response?.data || 'An error occurred';
			setError(message);
			setIsLoading(false);
		}
	});

	const handleOtpVerify = async (otp: string) => {
		setError(null);
		setIsLoading(true);
		try {
			const user = await authService.verifyOtp(userPhone, otp);
			window.dataLayer.push({
				event: 'successful_sign_up',
				user_id: user.id,
				phone: user.phone,
				name: user.name,
				account_id: user.account,
			});
			setUser(user);
			navigate('/home');
		} catch (error: any) {
			setIsLoading(false);
			setError(error?.response?.data?.message || 'Invalid verification code');
		}
	};

	const handleResendOtp = async () => {
		setError(null);
		try {
			const captchaToken = await getCaptchaToken('resend_otp');
			await authService.sendOtp(userPhone, captchaToken);
		} catch (error: any) {
			setError(error?.response?.data?.message || 'Failed to resend OTP');
		}
	};

	if (showOtpVerification) {
		return (
			<Flex flex={1} direction="column" gap={3}>
				{error && <AlertMessage status="error">{error}</AlertMessage>}
				<OtpVerification
					phone={userPhone}
					onVerify={handleOtpVerify}
					onResend={handleResendOtp}
					isLoading={isLoading}
				/>
				<Flex justifyContent="center" mt={4}>
					<Button
						variant="link"
						color="secondary"
						onClick={() => {
							setShowOtpVerification(false);
							setError(null);
						}}
					>
						Back to sign up
					</Button>
				</Flex>
			</Flex>
		);
	}

	return (
		<Flex flex={1} direction="column" gap={3}>
			<FormProvider {...formMethods}>
				{error && <AlertMessage status="error">{error}</AlertMessage>}
				<form onSubmit={onSubmit} autoComplete="off">
					<Flex
						flexDirection="column"
						justifyContent="center"
						height="100%"
						gap="28px"
					>
						<StringInputHook
							label="Phone Number"
							name="phone"
							type="tel"
							placeholder="+1 234 567 8900"
							required
							inputProps={{
								size: 'md',
								fontSize: '14px',
							}}
						/>
						<Text fontSize="xs" color="gray.text" mt={-4}>
							Enter phone number with country code (e.g., +1 for US)
						</Text>
						<Text fontSize="xs" color="gray.500" mt={-3}>
							If you previously created an account with email, you can still log in
						</Text>

						<StringInputHook
							label="Name"
							name="name"
							placeholder="Your full name"
							required
							inputProps={{
								size: 'md',
								fontSize: '14px',
								autoComplete: 'new-password',
							}}
						/>
						<StringInputHook
							label="Company name"
							name="accountName"
							placeholder="Your company name"
							required
							inputProps={{
								size: 'md',
								fontSize: '14px',
								autoComplete: 'new-password',
							}}
						/>

						<Flex
							justifyContent="space-between"
							alignItems="center"
							w="full"
							gap={1}
						>
							<Text fontSize="sm">
								By clicking "Sign Up" you agree to our{' '}
								<Link
									target="_blank"
									href="https://www.fusionos.ai/terms-of-service"
									textDecor="underline"
									color="secondary"
								>
									Terms of Service
								</Link>{' '}
								and{' '}
								<Link
									target="_blank"
									textDecor="underline"
									href="https://www.fusionos.ai/privacy-policy"
									color="secondary"
								>
									Privacy Notice
								</Link>
							</Text>
							<Button
								variant="orangeSolid"
								minW="120px"
								type="submit"
								isLoading={isLoading}
								loadingText="Loading..."
							>
								Sign Up
							</Button>
						</Flex>
					</Flex>
				</form>
			</FormProvider>
		</Flex>
	);
};

export default SignUpForm;
